import React, { useState } from "react"; // Добавляем useState
import DynamicTable from "./DynamicTableEdit";
import Navbar from "../components/Navbar";
import { toast, ToastContainer } from "react-toastify";
import RichTextComponent from "../components/RichTextComponent";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const EditForm = (props) => {
  const { report, table, data, form, opened, username } = props;
  const [isExporting, setIsExporting] = useState(false); // Состояние для экспорта PDF
  const [isExportingExcel, setIsExportingExcel] = useState(false); // Состояние для экспорта Excel

  const initialValues = data.reduce((acc, item) => {
    const [key, value] = Object.entries(item)[0];
    acc[key] = value;
    return acc;
  }, {});

  const handleSubmit = (formData) => {
    const transformedData = Object.keys(formData).map((key) => ({
      [key]: formData[key],
    }));

    const dataToSend = {
      report_id: report.id,
      data: transformedData,
    };

    fetch(`/reports/${report.id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dataToSend),
    })
      .then(async (response) => {
        if (response.ok) {
          toast.success("Форма успешно обновлена!");
        } else {
          const errorData = await response.json();
          toast.error(
            `Ошибка при обновлении формы: ${
              errorData.error || "Неизвестная ошибка"
            }`
          );
        }
      })
      .catch((error) => {
        console.error("Ошибка сети:", error);
        toast.error("Ошибка сети. Попробуйте снова.");
      });
  };

  const exportToPDF = async () => {
    setIsExporting(true); // Устанавливаем состояние "Экспортируется"
    const content = document.getElementById("pdf-content");

    const clone = content.cloneNode(true);
    const inputs = clone.querySelectorAll("input");
    inputs.forEach((input) => {
      const p = document.createElement("p");
      p.textContent = input.value;
      input.parentNode.replaceChild(p, input);
    });

    const offScreenDiv = document.createElement("div");
    offScreenDiv.style.position = "fixed";
    offScreenDiv.style.top = "-9999px";
    offScreenDiv.style.left = "-9999px";
    offScreenDiv.appendChild(clone);
    document.body.appendChild(offScreenDiv);

    const canvas = await html2canvas(clone, {
      scale: 1, // Уменьшаем масштаб для снижения размера
      ignoreElements: (element) => element.classList.contains("no-print"),
    });

    document.body.removeChild(offScreenDiv);

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Используем JPEG с качеством 0.7 для уменьшения размера
    const imgData = canvas.toDataURL("image/jpeg", 0.7);

    const scaleFactor = pdfWidth / canvasWidth;
    const scaledHeight = canvasHeight * scaleFactor;

    let yPosition = 0;

    while (yPosition < scaledHeight) {
      const nextHeight = Math.min(scaledHeight - yPosition, pdfHeight);

      const croppedCanvas = document.createElement("canvas");
      croppedCanvas.width = canvasWidth;
      croppedCanvas.height = Math.floor(nextHeight / scaleFactor);

      const ctx = croppedCanvas.getContext("2d");
      ctx.drawImage(
        canvas,
        0,
        yPosition / scaleFactor,
        canvasWidth,
        croppedCanvas.height,
        0,
        0,
        canvasWidth,
        croppedCanvas.height
      );

      const croppedImgData = croppedCanvas.toDataURL("image/jpeg", 0.7);

      pdf.addImage(
        croppedImgData,
        "JPEG", // Меняем на JPEG
        0,
        0,
        pdfWidth,
        (croppedCanvas.height * pdfWidth) / croppedCanvas.width
      );

      yPosition += nextHeight;

      if (yPosition < scaledHeight) {
        pdf.addPage();
      }
    }

    pdf.save("report.pdf");
    setIsExporting(false); // Сбрасываем состояние после завершения
  };

  // --- Экспорт в Excel с оформлением: рамки, жирная шапка, название сверху ---
  // Числовое значение пишем числом (чтобы в Excel считалось), иначе — текстом.
  const toCell = (raw) => {
    if (raw === null || raw === undefined) return "";
    const s = String(raw).trim();
    if (s === "") return "";
    if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
    return s;
  };

  // Достаём простой текст из «богатого текста» (HTML), сохраняя переносы между абзацами.
  const richToText = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = String(html)
      .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n");
    return (tmp.textContent || "")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]+\n/g, "\n")
      .trim();
  };

  // Ширина раздела = max(ширина строк данных, суммарный colSpan заголовков)
  const sectionWidth = (section) => {
    const rows = section.rows || [];
    const headers = section.headers || [];
    let dataWidth = 0;
    rows.forEach((row) => {
      const w = 2 + ((row.values && row.values.length) || 0);
      if (w > dataWidth) dataWidth = w;
    });
    let headerWidth = 0;
    headers.forEach((hr) => {
      let w = 0;
      hr.forEach((c) => (w += c.colSpan || 1));
      if (w > headerWidth) headerWidth = w;
    });
    return Math.max(dataWidth, headerWidth, 1);
  };

  const exportToExcel = async () => {
    setIsExportingExcel(true);
    try {
      // Подгружаем ExcelJS только сейчас (по клику), а не вместе со страницей —
      // библиотека уйдёт в отдельный файл, который webpack скачает по требованию.
      const { default: ExcelJS } = await import(
        /* webpackChunkName: "exceljs" */ "exceljs/dist/exceljs.min.js"
      );

      const sections = table || [];

      // "Эффективные" значения полей: сначала копия сохранённых, затем поверх —
      // пересчитанные поля из правил type:"sum" (сумма нескольких полей-источников).
      // Это те же правила, что применяет экран при загрузке (DynamicTableEdit.jsx,
      // applyRules) — их значения не хранятся напрямую, а всегда вычисляются заново,
      // поэтому напрямую из initialValues их брать нельзя.
      const effectiveValues = { ...initialValues };
      const applySumRules = () => {
        sections.forEach((section) => {
          (section.rules || []).forEach((rule) => {
            if (rule.type === "sum") {
              const sum = (rule.sources || []).reduce(
                (acc, src) => acc + (parseFloat(effectiveValues[src]) || 0),
                0
              );
              effectiveValues[rule.target] = sum.toString();
            }
          });
        });
      };
      // Несколько проходов — на случай, если одна сумма зависит от другой суммы.
      applySumRules();
      applySumRules();
      applySumRules();

      const thin = { style: "thin", color: { argb: "FF000000" } };
      const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

      let globalWidth = 1;
      sections.forEach((s) => {
        const w = sectionWidth(s);
        if (w > globalWidth) globalWidth = w;
      });

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Отчет");

      const colLen = new Array(globalWidth).fill(0);
      const track = (colIdx, val) => {
        const len = String(val == null ? "" : val).length;
        if (colIdx < globalWidth && len > colLen[colIdx]) colLen[colIdx] = len;
      };

      let r = 1; // текущая строка (1-индексация)

      // Блок текста на всю ширину (для «Заголовка», названия и «Информации»)
      const addBlock = (text, opts) => {
        const o = opts || {};
        if (!text) return;
        const cell = ws.getCell(r, 1);
        cell.value = text;
        cell.font = { bold: !!o.bold, size: o.size || 11 };
        cell.alignment = {
          horizontal: o.align || "left",
          vertical: "middle",
          wrapText: true,
        };
        if (globalWidth > 1) ws.mergeCells(r, 1, r, globalWidth);
        const lines = String(text).split("\n").length;
        ws.getRow(r).height = o.height != null ? o.height : Math.max(18, lines * 16);
        r += 1;
      };

      // «Заголовок» (шапка-«приложение») — слева сверху
      const headerText = richToText(form.header);
      if (headerText) {
        addBlock(headerText, { align: "left", height: 90 });
        r += 1; // отступ
      }

      // Название формы — жирным, по левому краю
      addBlock((form.form_name || "Отчет").toString(), {
        bold: true,
        size: 9,
        align: "left",
      });

      // «Информация» — под названием, слева
      const infoText = richToText(form.information);
      if (infoText) addBlock(infoText, { align: "left", height: 90 });

      r += 1; // пустой отступ перед таблицей

      sections.forEach((section) => {
        const headers = section.headers || [];
        const rows = section.rows || [];
        const width = sectionWidth(section);
        const blockStart = r;

        // Заголовок раздела
        if (section.title) {
          const cell = ws.getCell(r, 1);
          cell.value = section.title;
          cell.font = { bold: true };
          cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
          if (width > 1) ws.mergeCells(r, 1, r, width);
          r += 1;
        }

        // Шапка таблицы (rowSpan/colSpan): жирная, серый фон, по центру
        const occupied = {};
        const hStart = r;
        headers.forEach((hr, hrIndex) => {
          const rr = hStart + hrIndex;
          let c = 1;
          hr.forEach((hcell) => {
            while (occupied[rr + "," + c]) c += 1;
            const rs = hcell.rowSpan || 1;
            const cs = hcell.colSpan || 1;
            const cell = ws.getCell(rr, c);
            cell.value = hcell.text != null ? hcell.text : "";
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
            if (rs > 1 || cs > 1) ws.mergeCells(rr, c, rr + rs - 1, c + cs - 1);
            for (let dr = 0; dr < rs; dr += 1) {
              for (let dc = 0; dc < cs; dc += 1) {
                occupied[rr + dr + "," + (c + dc)] = true;
              }
            }
            c += cs;
          });
        });
        r = hStart + headers.length;

        // Строки данных: № | описание | значения
        rows.forEach((row) => {
          const vals = [
            row.rowNumber != null ? row.rowNumber : "",
            row.description != null ? row.description : "",
          ];
          (row.values || []).forEach((name) => vals.push(toCell(effectiveValues[name])));
          for (let i = 0; i < width; i += 1) {
            const cell = ws.getCell(r, i + 1);
            const v = i < vals.length ? vals[i] : "";
            cell.value = v;
            cell.alignment = { vertical: "middle", wrapText: i === 1 };
            track(i, v);
          }
          r += 1;
        });

        // Рамки на весь блок раздела (шапка + данные)
        const blockEnd = r - 1;
        for (let rowNum = blockStart; rowNum <= blockEnd; rowNum += 1) {
          for (let colNum = 1; colNum <= width; colNum += 1) {
            ws.getCell(rowNum, colNum).border = allBorders;
          }
        }

        r += 1; // пустая строка между разделами
      });

      // Ширина столбцов
      for (let i = 0; i < globalWidth; i += 1) {
        let w = colLen[i] + 2;
        if (w < 8) w = 8;
        if (w > 60) w = 60;
        ws.getColumn(i + 1).width = w;
      }

      // Генерация файла и скачивание
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Имя файла: ФИО_ДатаПоследнейПравки_НазваниеФормы.xlsx
      const pad2 = (n) => String(n).padStart(2, "0");
      const formatDate = (input) => {
        const d = new Date(input);
        if (isNaN(d.getTime())) return "";
        return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
      };
      const sanitize = (s) =>
        (s || "").toString().replace(/[\\/:*?"<>|]+/g, "_").trim();

      const fioPart = sanitize(username);
      const datePart = sanitize(formatDate(report.date_edited));
      const formPart = sanitize(form.short_name || form.form_name || "report");

      const base = [fioPart, datePart, formPart].filter(Boolean).join("_").slice(0, 150);
      a.download = (base || "report") + ".xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Ошибка экспорта в Excel:", e);
      toast.error("Не удалось сформировать Excel");
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div className="min-h-full">
      <ToastContainer />
      <div className="pb-32">
        <Navbar props={props} />
      </div>

      <main className="-mt-32">
        <div
          className="mx-auto container max-w-screen-2xl px-4 pb-12"
          id="pdf-content"
        >
          <header className="flex mt-20 flex-col md:flex-row justify-between items-center border-b border-gray-300 pb-4 mb-5">
            <div className="text-left md:ml-4">
              <button
                onClick={exportToPDF}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 no-print disabled:bg-gray-400"
                disabled={isExporting} // Блокируем кнопку во время экспорта
              >
                {isExporting ? "Экспортируется..." : "Экспорт в PDF"}
              </button>
              <button
                onClick={exportToExcel}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 no-print ml-2 disabled:bg-gray-400"
                disabled={isExportingExcel}
              >
                {isExportingExcel ? "Загрузка..." : "Экспорт в Excel"}
              </button>
            </div>
            <div className="text-right md:mr-4 mt-4 md:mt-0">
              <RichTextComponent richText={form.header} />
            </div>
          </header>
          <main className="mb-5">
            <h1 className="text-center text-lg md:text-xl font-bold mb-6">
              {form.form_name}
            </h1>
            <div className="text-sm md:text-base space-y-2">
              <RichTextComponent richText={form.information} />
            </div>
          </main>
          <DynamicTable
            sections={table}
            onSubmit={handleSubmit}
            initialValues={initialValues}
          />
        </div>
      </main>
    </div>
  );
};

export default EditForm;
